import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../api/api";
import { hooks } from "../../hooks";
import { Routes, TabScreens } from "../../routes";
import { useDispatch } from 'react-redux';
import { actions } from '../../store/actions';
import { usePermission } from '../../hooks/usePermission';
import { SearchSvg } from '../../assets/svg/SearchSvg';

interface PatrolType {
  id: number;
  name: string;
  description: string;
  in: string;
  out: string;
  day: number;
  early_in: number;
  grace_in: number;
}

interface FormModalProps {
  show: boolean;
  editId: number | null;
  formData: {
    name: string;
    description: string;
    inTime: string;
    outTime: string;
    earlyIn: number;
    graceIn: number;
  };
  onClose: () => void;
  onSave: () => void;
  onFormChange: (field: string, value: string | number) => void;
}

const FormModal: React.FC<FormModalProps> = ({ 
  show, 
  editId, 
  formData, 
  onClose, 
  onSave, 
  onFormChange 
}) => {
  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="form-modal">
        <div className="form-modal-header">
          <h3 className="form-modal-title">
            {editId ? "Edit Patroli" : "Tambah Patroli"}
          </h3>
          <button 
            onClick={onClose}
            className="form-modal-close"
          >
            ✕
          </button>
        </div>

        <div className="form-modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Nama Patroli *</label>
              <input 
                type="text" 
                placeholder="Masukkan nama patroli" 
                className="input-field" 
                value={formData.name} 
                onChange={(e) => onFormChange('name', e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Deskripsi</label>
              <input 
                type="text" 
                placeholder="Masukkan deskripsi patroli" 
                className="input-field" 
                value={formData.description} 
                onChange={(e) => onFormChange('description', e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Waktu Masuk (In) *</label>
              <input 
                type="time" 
                className="input-field" 
                value={formData.inTime} 
                onChange={(e) => onFormChange('inTime', e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Waktu Keluar (Out) *</label>
              <input 
                type="time" 
                className="input-field" 
                value={formData.outTime} 
                onChange={(e) => onFormChange('outTime', e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Early In (menit)</label>
              <input 
                type="number" 
                placeholder="Masukkan early in dalam menit" 
                className="input-field" 
                value={formData.earlyIn} 
                onChange={(e) => onFormChange('earlyIn', parseInt(e.target.value) || 0)} 
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Grace In (menit)</label>
              <input 
                type="number" 
                placeholder="Masukkan grace in dalam menit" 
                className="input-field" 
                value={formData.graceIn} 
                onChange={(e) => onFormChange('graceIn', parseInt(e.target.value) || 0)} 
                min="0"
              />
            </div>
          </div>
        </div>

        <div className="form-modal-actions">
          <button 
            onClick={onClose}
            className="form-modal-cancel"
          >
            Batal
          </button>
          <button 
            onClick={onSave}
            className="form-modal-save"
          >
            {editId ? "Update Patrol" : "Simpan Patrol"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  perPage: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, totalItems, perPage, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(
      <button
        key={i}
        onClick={() => onPageChange(i)}
        className={`pagination-page ${currentPage === i ? 'pagination-active' : ''}`}
      >
        {i}
      </button>
    );
  }

  const startItem = ((currentPage - 1) * perPage) + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  return (
    <div className="pagination-top-section">
      <div className="pagination-column-layout">
        <div className="pagination-info-top">
          Menampilkan {startItem} - {endItem} dari {totalItems} patroli
        </div>
        
        <div className="pagination-controls-center">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-nav"
          >
            ← Prev
          </button>
          
          {startPage > 1 && (
            <>
              <button onClick={() => onPageChange(1)} className="pagination-page">1</button>
              {startPage > 2 && <span className="pagination-ellipsis">...</span>}
            </>
          )}
          
          {pages}
          
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
              <button onClick={() => onPageChange(totalPages)} className="pagination-page">
                {totalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-nav"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export const PatrolFormTable: React.FC = () => {
  const navigate = hooks.useNavigate();
  const dispatch = useDispatch();
  const [patrols, setPatrols] = useState<PatrolType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patrolToDelete, setPatrolToDelete] = useState<PatrolType | null>(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [earlyIn, setEarlyIn] = useState<number>(0);
  const [graceIn, setGraceIn] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const { canIndex, canStore, canUpdate, canDelete } = usePermission('patrol_index');

  const handleFormChange = (field: string, value: string | number) => {
    switch (field) {
      case 'name':
        setName(value as string);
        break;
      case 'description':
        setDescription(value as string);
        break;
      case 'inTime':
        setInTime(value as string);
        break;
      case 'outTime':
        setOutTime(value as string);
        break;
      case 'earlyIn':
        setEarlyIn(value as number);
        break;
      case 'graceIn':
        setGraceIn(value as number);
        break;
    }
  };

  const formData = {
    name,
    description,
    inTime,
    outTime,
    earlyIn,
    graceIn
  };

  const fetchPatrols = async (searchFilter = "", page = 1) => {
    try {
      setLoading(true);
      const params: any = {
        sort_by: "created_at",
        sort_type: "desc",
        per_page: perPage,
        page: page
      };

      if (searchFilter) {
        params.filter = searchFilter;
      }

      const res = await axiosInstance.get("/api/v1/patrol/index", {
        params,
      });
      
      setPatrols(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
      setTotalItems(res.data.total || 0);
      setCurrentPage(res.data.current_page || page);
      
    } catch (err) {
      console.error("Error fetching patrols:", err);
      showCustomError("Gagal memuat data patroli. Silakan coba lagi.");
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate(Routes.SignIn);
    } else {
      fetchPatrols();
    }
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);

    if (query.length < 3 && query.length > 0) {
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setCurrentPage(1);
    
    const timeoutId = setTimeout(() => {
      fetchPatrols(query, 1);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPatrols(searchQuery, page);
  };

  const resetForm = () => {
    setEditId(null);
    setName("");
    setDescription("");
    setInTime("");
    setOutTime("");
    setEarlyIn(0);
    setGraceIn(0);
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const [hour, minute] = time.split(":");
    return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  };

  const showCustomSuccess = (message: string) => {
    setModalTitle("Sukses");
    setModalMessage(message);
    setShowSuccessModal(true);
  };

  const showCustomError = (message: string) => {
    setModalTitle("Error");
    setModalMessage(message);
    setShowErrorModal(true);
  };

  const showCustomValidation = (message: string) => {
    setModalTitle("Peringatan");
    setModalMessage(message);
    setShowValidationModal(true);
  };

  const closeAllModals = () => {
    setShowSuccessModal(false);
    setShowErrorModal(false);
    setShowValidationModal(false);
    setShowDeleteModal(false);
    setShowFormModal(false);
    setModalMessage("");
    setModalTitle("");
  };

  const handleSavePatrol = async () => {
    if (!name || !inTime || !outTime) {
      showCustomValidation("Nama, waktu masuk, dan waktu keluar wajib diisi!");
      return;
    }

    const payload = {
      name,
      description,
      in: formatTime(inTime),
      out: formatTime(outTime),
      day: 0,
      early_in: earlyIn,
      grace_in: graceIn,
    };

    try {
      if (editId) {
        await axiosInstance.post(`/api/v1/patrol/update/${editId}`, payload);
        showCustomSuccess("Patrol berhasil diupdate!");
      } else {
        await axiosInstance.post("/api/v1/patrol/store", payload);
        showCustomSuccess("Patrol berhasil ditambahkan!");
      }

      await fetchPatrols(searchQuery, currentPage);
      setShowFormModal(false);
      resetForm();
    } catch (err: any) {
      console.error("Error saving patrol:", err.response?.data || err);
      showCustomError("Gagal simpan patrol. Silakan cek console/log untuk detail.");
    }
  };

  const handleEdit = (patrol: PatrolType) => {
    setEditId(patrol.id);
    setName(patrol.name);
    setDescription(patrol.description);
    setInTime(patrol.in);
    setOutTime(patrol.out);
    setEarlyIn(patrol.early_in || 0);
    setGraceIn(patrol.grace_in || 0);
    setShowFormModal(true);
  };

  const handleAddNew = () => {
    resetForm();
    setShowFormModal(true);
  };

  const handleDeleteClick = (patrol: PatrolType) => {
    setPatrolToDelete(patrol);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!patrolToDelete) return;
    setLoading(true);

    try {
      await axiosInstance.delete(`/api/v1/patrol/destroy/${patrolToDelete.id}`);
      showCustomSuccess("Patroli berhasil dihapus!");
      
      if (patrols.length === 1 && currentPage > 1) {
        await fetchPatrols(searchQuery, currentPage - 1);
      } else {
        await fetchPatrols(searchQuery, currentPage);
      }
      
      setShowDeleteModal(false);
      setPatrolToDelete(null);
    } catch (err: any) {
      console.error("Error deleting patrol:", err.response?.data || err);

      const backendMessage = err.response?.data?.message;

      if (backendMessage === "Patrol has components") {
        showCustomError(
          "Patroli ini masih memiliki komponen. Hapus dulu semua komponen terkait sebelum menghapus patroli."
        );
      } else {
        showCustomError(
          "Gagal menghapus patroli. Silakan coba lagi nanti atau hubungi administrator."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPatrolToDelete(null);
  };

  const handleBack = () => {
    dispatch(actions.setScreen(TabScreens.Admin))
    navigate("/tab-navigator", { replace: true })
  };

const PatrolCard: React.FC<{
  patrol: PatrolType;
  onEdit: (patrol: PatrolType) => void;
  onDelete: (patrol: PatrolType) => void;
}> = ({ patrol, onEdit, onDelete }) => {
  return (
    <div
      className="patrol-card"
      onClick={() => navigate(`${Routes.PatrolDetailPage}/${patrol.id}`)}
    >
      <div className="card-content">
        <div className="card-header">
          <div className="card-info">
            <h4 className="patrol-name">{patrol.name}</h4>
            <p className="patrol-description">{patrol.description || "Patroli keamanan"}</p>
            <div className="time-info">
              <div className="time-group">
                <span className="time-label">Mulai: </span>
                <span className="time-value">{patrol.in || "-"}</span>
              </div>
              <div className="time-group">
                <span className="time-label">Selesai: </span>
                <span className="time-value">{patrol.out || "-"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-actions" onClick={(e) => e.stopPropagation()}>
        {canUpdate && <button className="btn-edit" onClick={() => onEdit(patrol)}>Ubah</button>}
        {canDelete && <button className="btn-delete" onClick={() => onDelete(patrol)}>Hapus</button>}
      </div>
    </div>
  );
};

  const PatrolList: React.FC<{
    data: PatrolType[];
    onEdit: (patrol: PatrolType) => void;
    onDelete: (patrol: PatrolType) => void;
  }> = ({ data, onEdit, onDelete }) => {
    return (
      <div className="cards-container">
        {data.map((p) => (
          <PatrolCard key={p.id} patrol={p} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    );
  };

  const CustomModal = ({ type, title, message, onConfirm, onClose }: {
    type: 'success' | 'error' | 'validation' | 'delete';
    title: string;
    message: string;
    onConfirm?: () => void;
    onClose: () => void;
  }) => {


    const getConfirmButtonColor = () => {
      switch (type) {
        case 'success': return 'modal-success-button';
        case 'error': return 'modal-error-button';
        case 'validation': return 'modal-warning-button';
        case 'delete': return 'modal-delete-button';
        default: return 'modal-primary-button';
      }
    };

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">

            <h3 className="modal-title">{title}</h3>
          </div>
          <div className="modal-body">
            <p className="modal-text">{message}</p>
          </div>
          <div className="modal-actions">
            {type === 'delete' ? (
              <>
                <button onClick={onClose} className="modal-cancel-button">
                  Batal
                </button>
                <button onClick={onConfirm} className={getConfirmButtonColor()}>
                  Ya, Hapus
                </button>
              </>
            ) : (
              <button onClick={onClose} className={getConfirmButtonColor()}>
                Tutup
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="patrol-container">
        {showDeleteModal && (
          <CustomModal
            type="delete"
            title="Hapus Jadwal Patroli"
            message={`Apakah Anda yakin ingin menghapus jadwal patroli "${patrolToDelete?.name}"? Tindakan ini tidak dapat dibatalkan dan semua data yang terkait akan hilang.`}
            onConfirm={handleDeleteConfirm}
            onClose={handleDeleteCancel}
          />
        )}

        {showSuccessModal && (
          <CustomModal
            type="success"
            title={modalTitle}
            message={modalMessage}
            onClose={() => setShowSuccessModal(false)}
          />
        )}

        {showErrorModal && (
          <CustomModal
            type="error"
            title={modalTitle}
            message={modalMessage}
            onClose={() => setShowErrorModal(false)}
          />
        )}

        {showValidationModal && (
          <CustomModal
            type="validation"
            title={modalTitle}
            message={modalMessage}
            onClose={() => setShowValidationModal(false)}
          />
        )}
        <FormModal
          show={showFormModal}
          editId={editId}
          formData={formData}
          onClose={() => {
            setShowFormModal(false);
            resetForm();
          }}
          onSave={handleSavePatrol}
          onFormChange={handleFormChange}
        />

        {/* Header */}
        <header className="header">
          <div className="header-content">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <button 
                onClick={handleBack}
                className="back-button"
              >
                <span className="back-icon">←</span>
              </button>
              <div className="header-info">
                <h1 className="header-title">Manajemen Patroli</h1>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="main-content">
          <div className="section-header">
            {canStore && (
              <button onClick={handleAddNew} className="add-button">
                <span className="add-icon">+</span>
                <span>Tambah Patroli</span>
              </button>
            )}
          </div>

          {/* Search Bar*/}
          <div className="search-container">
            <div className="search-input-wrapper">
              <div className="search-icon-wrapper">
                {searchLoading ? (
                  <div className="search-loading-spinner" />
                ) : (
                  <SearchSvg className="search-svg" />
                )}
              </div>
              <input
                type="text"
                placeholder="Cari patroli berdasarkan nama..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* PAGINATION */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            perPage={perPage}
            onPageChange={handlePageChange}
          />

          {/* Content Section */}
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Memuat data patroli...</p>
            </div>
          ) : (
            <div className="content-section">
              {patrols.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🛡️</div>
                  <h3>
                    {searchQuery ? "Patroli Tidak Ditemukan" : "Belum Ada Patroli"}
                  </h3>
                  <p>
                    {searchQuery 
                      ? `Tidak ada patroli yang sesuai dengan pencarian "${searchQuery}"`
                      : "Mulai dengan menambahkan patroli pertama Anda"
                    }
                  </p>
                  {!searchQuery && (
                    <button onClick={handleAddNew} className="add-button">+ Tambah Patroli Pertama</button>
                  )}
                  {searchQuery && (
                    <button 
                      onClick={() => {
                        setSearchQuery("");
                        fetchPatrols();
                      }} 
                      className="add-button"
                    >
                      Tampilkan Semua Patroli
                    </button>
                  )}
                </div>
              ) : (
                <div className="scroll-container">
                  {canIndex ? (
                    <>
                      {searchQuery && (
                        <div className="search-results-info">
                          <p>
                            Menampilkan {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalItems)} dari {totalItems} hasil untuk "{searchQuery}"
                          </p>
                          <button 
                            onClick={() => {
                              setSearchQuery("");
                              fetchPatrols();
                            }} 
                            className="clear-search-button"
                          >
                            Hapus Pencarian
                          </button>
                        </div>
                      )}
                      
                      <PatrolList 
                        data={patrols} 
                        onEdit={canUpdate ? handleEdit : () => {}} 
                        onDelete={canDelete ? handleDeleteClick : () => {}} 
                      />
                    </>
                  ) : (
                    <p>Anda tidak memiliki akses untuk melihat jadwal patroli.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      <style>{`
        .patrol-container {
          height: 100vh;
          background-color: #f8fafc;
          font-family: 'Inter', sans-serif;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
        }

        /* Search Bar Styles */
        .search-container {
          margin-bottom: 0;
          flex-shrink: 0;
          width: 100%;
        }

        .search-input-wrapper {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 48px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
          height: 48px;
          box-sizing: border-box;
        }

        .search-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .search-icon-wrapper {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }

        .search-svg {
          width: 20px;
          height: 20px;
          color: #64748b;
        }

        .search-loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .search-results-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding: 12px 16px;
          background-color: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .search-results-info p {
          margin: 0;
          color: #475569;
          font-size: 14px;
        }

        .clear-search-button {
          background: #6b7280;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .clear-search-button:hover {
          background: #4b5563;
        }

        /* PAGINATION */
        .pagination-top-section {
          background: #f8fafc;
          padding: 16px 0;
          margin-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .pagination-column-layout {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
        }

        .pagination-info-top {
          text-align: left;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          padding: 0 16px;
        }

        .pagination-controls-center {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 0 16px;
        }

        .pagination-nav, .pagination-page {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
          min-width: 40px;
          text-align: center;
        }

        .pagination-nav:hover:not(:disabled), 
        .pagination-page:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }

        .pagination-nav:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-active {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
        }

        .pagination-ellipsis {
          padding: 8px 4px;
          color: #64748b;
          font-weight: bold;
        }

        .header {
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          position: sticky;
          top: 0;
          zIndex: 10;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
          width: 100%;
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px;
          width: 100%;
        }

        .back-button {
          background: none;
          border: none;
          padding: 8px;
          border-radius: 50%;
          cursor: pointer;
          color: #0f172a;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }

        .back-button:hover {
          background-color: #f1f5f9;
        }

        .back-icon {
          font-size: 24px;
          font-weight: bold;
        }

        .header-info {
          display: flex;
          flex-direction: column;
        }

        .header-title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 4px 0;
        }

        .header-subtitle {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .main-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-shrink: 0;
          width: 100%;
        }

        .section-title {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .add-button {
          background-color: #2563eb;
          color: white;
          font-weight: 600;
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          gap: 8px;
          box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
          height: 48px;
        }

        .add-button:hover {
          background-color: #1d4ed8;
        }

        .add-icon {
          font-size: 16px;
          font-weight: bold;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          color: #64748b;
          flex: 1;
          width: 100%;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        .content-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
        }
        .scroll-container {
          flex: 1;
          overflow-y: auto;
          padding-right: 4px;
          width: 100%;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1;
          width: 100%;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          color: #374151;
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
        }

        .empty-state p {
          margin: 0 0 20px 0;
          color: #6b7280;
          font-size: 16px;
        }

        .cards-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-bottom: 8px;
          width: 100%;
        }

        .patrol-card {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
          padding: 16px;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
          box-sizing: border-box;
        }

        .patrol-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(0,0,0,0.1);
        }

        .card-content {
          margin-bottom: 16px;
          width: 100%;
        }

        .card-header {
          display: block;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          width: 100%;
        }

        .card-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
          width: 100%;
        }

        .patrol-name {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
          display: block;
          overflow: visible;
          white-space: normal;
          word-break: break-word;
          width: 100%;
          line-height: 1.4;
        }

        .patrol-description {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          display: block;
          overflow: visible;
          white-space: normal;
          word-break: break-word;
          width: 100%;
          line-height: 1.4;
        }

        .time-info {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-top: 8px;
        }

        .time-group {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .time-label {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        .time-value {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }

        .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
          width: 100%;
        }

        .btn-edit {
          background: #dbeafe;
          color: #2563eb;
          font-weight: 600;
          padding: 8px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.3s;
        }

        .btn-edit:hover {
          background: #bfdbfe;
        }

        .btn-delete {
          background: #fee2e2;
          color: #dc2626;
          font-weight: 600;
          padding: 8px 20px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.3s;
        }

        .btn-delete:hover {
          background: #fecaca;
        }

        .form-modal {
          background: white;
          border-radius: 16px;
          padding: 0;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
          animation: modalSlideIn 0.3s ease-out;
        }

        .form-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 20px;
        }

        .form-modal-title {
          font-size: 20px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }

        .form-modal-close {
          background: none;
          border: none;
          font-size: 20px;
          padding: 4px;
          border-radius: 4px;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          transition: background-color 0.2s;
        }

        .form-modal-close:hover {
          background-color: #f1f5f9;
        }

        .form-modal-body {
          padding: 0 24px;
          max-height: 60vh;
          overflow-y: auto;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group:nth-child(3) { grid-column: 1; }
        .form-group:nth-child(4) { grid-column: 2; }
        .form-group:nth-child(5) { grid-column: 1; }
        .form-group:nth-child(6) { grid-column: 2; }

        .form-label {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .input-field {
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
          width: 100%;
          box-sizing: border-box;
          background: white;
        }

        .input-field:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }

        .form-modal-actions {
          padding: 24px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          border-top: 1px solid #e2e8f0;
        }

        .form-modal-cancel {
          padding: 12px 20px;
          background: #6b7280;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .form-modal-cancel:hover {
          background: #4b5563;
        }

        .form-modal-save {
          padding: 12px 20px;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .form-modal-save:hover {
          background: #1d4ed8;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 16px;
          padding: 0;
          max-width: 400px;
          width: 100%;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
          animation: modalSlideIn 0.3s ease-out;
        }

        .modal-header {
          padding: 24px 24px 0 24px;
          text-align: center;
        }

        .modal-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }

        .modal-body {
          padding: 24px;
          text-align: center;
        }

        .modal-text {
          font-size: 16px;
          color: #475569;
          margin: 0;
          line-height: 1.5;
        }

        .modal-actions {
          padding: 0 24px 24px 24px;
          display: flex;
          gap: 12px;
        }

        .modal-cancel-button {
          flex: 1;
          background: transparent;
          border: 1px solid #e2e8f0;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          color: #475569;
        }

        .modal-delete-button {
          flex: 1;
          background: #dc2626;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
        }

        .modal-success-button {
          flex: 1;
          background: #16a34a;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
        }

        .modal-error-button {
          flex: 1;
          background: #dc2626;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
        }

        .modal-warning-button {
          flex: 1;
          background: #f59e0b;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
        }

        .modal-primary-button {
          flex: 1;
          background: #2563eb;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(-10px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .modal-overlay {
          z-index: 1000;
        }

        .modal-overlay:has(.modal) {
          z-index: 1002;
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 12px;
          }

          .section-header {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }

          .search-input-wrapper {
            max-width: 100%;
          }

          .search-results-info {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }

          .pagination-controls-center {
            gap: 4px;
          }

          .pagination-nav, .pagination-page {
            padding: 6px 8px;
            font-size: 12px;
            min-width: 32px;
          }

          .form-modal {
            max-width: 90%;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group:nth-child(3),
          .form-group:nth-child(4),
          .form-group:nth-child(5),
          .form-group:nth-child(6) {
            grid-column: 1;
          }

          .form-modal-actions {
            flex-direction: column;
          }

          .card-header {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .time-info {
            text-align: left;
          }

          .modal-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
};